import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	INVESTOR_EMAIL_WORKFLOW_HANDLER_STORAGE_KEY,
	INVESTOR_RELEASE_WORKFLOW_HANDLER_STORAGE_KEY,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import type { WorkflowProgressContextValue, WorkflowProgressKind } from './WorkflowProgress.types';

const WorkflowProgressContext = createContext<WorkflowProgressContextValue | null>(null);

const initialKind = (
	emailHandlerId: string | null,
	releaseHandlerId: string | null
): WorkflowProgressKind | null => {
	if (emailHandlerId) {
		return 'email';
	}
	if (releaseHandlerId) {
		return 'release';
	}
	return null;
};

export const WorkflowProgressProvider = ({ children }: PropsWithChildren) => {
	const [emailHandlerId, setEmailHandlerId] = useState<string | null>(() =>
		sessionStorage.getItem(INVESTOR_EMAIL_WORKFLOW_HANDLER_STORAGE_KEY)
	);
	const [releaseHandlerId, setReleaseHandlerId] = useState<string | null>(() =>
		sessionStorage.getItem(INVESTOR_RELEASE_WORKFLOW_HANDLER_STORAGE_KEY)
	);
	const [lastStartedKind, setLastStartedKind] = useState<WorkflowProgressKind | null>(() =>
		initialKind(emailHandlerId, releaseHandlerId)
	);

	const startEmailWorkflow = useCallback((handlerId: string | null | undefined) => {
		if (!handlerId) {
			return;
		}
		sessionStorage.setItem(INVESTOR_EMAIL_WORKFLOW_HANDLER_STORAGE_KEY, handlerId);
		setEmailHandlerId(handlerId);
		setLastStartedKind('email');
	}, []);

	const startReleaseWorkflow = useCallback((handlerId: string | null | undefined) => {
		if (!handlerId) {
			return;
		}
		sessionStorage.setItem(INVESTOR_RELEASE_WORKFLOW_HANDLER_STORAGE_KEY, handlerId);
		setReleaseHandlerId(handlerId);
		setLastStartedKind('release');
	}, []);

	const finishEmailWorkflow = useCallback(() => {
		sessionStorage.removeItem(INVESTOR_EMAIL_WORKFLOW_HANDLER_STORAGE_KEY);
		setEmailHandlerId(null);
	}, []);

	const finishReleaseWorkflow = useCallback(() => {
		sessionStorage.removeItem(INVESTOR_RELEASE_WORKFLOW_HANDLER_STORAGE_KEY);
		setReleaseHandlerId(null);
	}, []);

	useEffect(
		() => () => {
			sessionStorage.removeItem(INVESTOR_EMAIL_WORKFLOW_HANDLER_STORAGE_KEY);
			sessionStorage.removeItem(INVESTOR_RELEASE_WORKFLOW_HANDLER_STORAGE_KEY);
		},
		[]
	);

	const value = useMemo<WorkflowProgressContextValue>(
		() => ({
			emailHandlerId,
			releaseHandlerId,
			lastStartedKind,
			startEmailWorkflow,
			startReleaseWorkflow,
			finishEmailWorkflow,
			finishReleaseWorkflow,
		}),
		[
			emailHandlerId,
			releaseHandlerId,
			lastStartedKind,
			startEmailWorkflow,
			startReleaseWorkflow,
			finishEmailWorkflow,
			finishReleaseWorkflow,
		]
	);

	return (
		<WorkflowProgressContext.Provider value={value}>
			{children}
		</WorkflowProgressContext.Provider>
	);
};

export const useWorkflowProgress = (): WorkflowProgressContextValue => {
	const context = useContext(WorkflowProgressContext);
	if (!context) {
		throw new Error('useWorkflowProgress must be used inside WorkflowProgressProvider');
	}
	return context;
};
